import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Button, Input, Select, message } from "antd";
import React, { useState } from "react";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { changeUserPassword, getUserDetails, updateUser } from "../../api/user";
import { Box } from "../../components/design/box.js";
import { ResponsiveSideDrawer } from "../../components/drawer";
import { FieldWrapper, ProfileUpload } from "../../components/inputs";
import { ValidationError } from "../../library/classes/exceptions/index.js";
import {
  Permission,
  RolePermission,
  UserRoles,
} from "../../library/constants/dynamic.js";
import {
  getFieldErrors,
  getFieldStatus,
} from "../../library/helpers/forms/index.js";
import Styles from "./user-details.module.scss";

const UserDetails = () => {
  const { user } = useLoaderData();
  const navigate = useNavigate();
  const [userType, setUserType] = useState(user.roles);
  const [permission, setPermission] = useState(user.permisions);
  const { errors = null } = useActionData() || {};

  const handleRolesChange = (roles) => {
    if (roles.length === 0) {
      setUserType([]);
      setPermission([]);
      return;
    }

    let newPermissions = [];
    setUserType(roles);
    roles.forEach((r) => {
      const rolePermissions =
        RolePermission.find(({ id }) => r === id)?.value || [];
      newPermissions = [...newPermissions, ...rolePermissions];
    });
    setPermission([...new Set(newPermissions)]);
  };

  return (
    <ResponsiveSideDrawer
      open={true}
      title={`User: ${user.name}`}
      onClose={() => {
        const searchParams = new URLSearchParams(location.search);
        navigate(
          {
            pathname: "..",
            search: `?${searchParams.toString()}`,
          },
          { replace: true, relative: "route" },
        );
      }}
    >
      <ProfileUpload user={user} />

      <Form method="post" className={Styles.formWrapper}>
        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Name" showRequired>
              <Input
                type="text"
                name="name"
                key={user.name}
                defaultValue={user.name}
                required
                status={getFieldStatus("name", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("name", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Email" showRequired>
              <Input
                type="email"
                name="email"
                key={user.email}
                defaultValue={user.email}
                required
                status={getFieldStatus("email", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("email", errors)}
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Meeting Link">
              <Input
                type="text"
                name="meeting_link"
                key={user.meeting_link}
                defaultValue={user.meeting_link}
              />
            </FieldWrapper>
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Roles">
              <Select
                key={user.roles}
                options={UserRoles}
                mode="multiple"
                fieldNames={{ value: "id", label: "name" }}
                defaultValue={userType}
                onChange={handleRolesChange}
                status={getFieldStatus("type", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="roles" value={userType} />
          </div>
        </div>

        <div className={Styles.fieldsWrapper}>
          <div className={Styles.inputField}>
            <FieldWrapper label="Permission">
              <Select
                key={user.permission}
                options={Permission}
                mode="multiple"
                value={permission}
                fieldNames={{ value: "id", label: "name" }}
                defaultValue={permission}
                onChange={(c) => setPermission(c)}
                status={getFieldStatus("type", errors)}
              />
            </FieldWrapper>
            {getFieldErrors("type", errors)}
            <input type="hidden" name="permission" value={permission} />
          </div>
        </div>

        <Button
          type="primary"
          name="action"
          value="updateUser"
          htmlType="submit"
        >
          Update
        </Button>
      </Form>

      <Box containerStyles={{ marginTop: "1rem" }}>
        <Form method="post" className={Styles.formWrapper}>
          <div className={Styles.fieldsWrapper}>
            <div className={Styles.inputField}>
              <FieldWrapper label="New Password" showRequired>
                <Input.Password
                  placeholder="New Password"
                  name="newpassword"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  status={getFieldStatus("name", errors)}
                  pattern="^(?=.*[0-9])(?=.*[\W_])(?=.*[a-zA-Z])(?=\S+$).{6,}$"
                  title="Password must contain at least one digit, one special character, one letter, and be at least 6 characters."
                  required
                />
              </FieldWrapper>
              {getFieldErrors("name", errors)}
            </div>
          </div>
          <Button
            type="primary"
            name="action"
            value="resetPassword"
            htmlType="submit"
          >
            Reset Password
          </Button>
        </Form>
      </Box>
    </ResponsiveSideDrawer>
  );
};

UserDetails.Actions = {
  updateUser: async ({ params, data }) => {
    const { userId } = params;
    const { name, email, roles, meeting_link, permission } = data;
    try {
      const { data } = await updateUser(userId, {
        name,
        email,
        meeting_link,
        user_roles:
          permission == ""
            ? roles.split(",")
            : roles.split(",").concat(permission.split(",")),
      });
      message.success("User Update!");
      return data;
    } catch (error) {
      message.error(error.message);
      if (error instanceof ValidationError) {
        return {
          errors: error.errors,
        };
      }
    }
    return false;
  },
  resetPassword: async ({ params, data }) => {
    const { userId } = params;
    const { newpassword } = data;

    try {
      const msg = await changeUserPassword(userId, newpassword);
      message.success(msg);
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        return {
          errors: error.errors,
        };
      }
    }
    return false;
  },
};

UserDetails.Loader = async ({ params }) => {
  const { userId } = params;
  const user = await getUserDetails(userId);
  return { user };
};

export default UserDetails;
